"use client";

import React, { useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Label } from "@/app/components/ui/label";
import { Input } from "@/app/components/ui/input";
import { Button } from "@/app/components/ui/button";
import { Switch } from "@/app/components/ui/switch";
import { Separator } from "@/app/components/ui/separator";
import { Copy, RotateCcw, ArrowLeftRight } from "lucide-react";
import {
  addDays,
  addMonths,
  addYears,
  eachDayOfInterval,
  format,
  intervalToDuration,
  isAfter,
  isBefore,
  isValid,
  isWeekend,
  parseISO,
  differenceInSeconds,
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
  differenceInWeeks,
  differenceInMonths,
  differenceInYears,
} from "date-fns";

function toLocalDateTimeValue(d: Date) {
  // Returns value compatible with <input type="datetime-local"> (no timezone info)
  const pad = (n: number) => String(n).padStart(2, "0");
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

export default function DateDiffPage() {
  const now = new Date();
  const [startValue, setStartValue] = useState<string>(toLocalDateTimeValue(now));
  const [endValue, setEndValue] = useState<string>(toLocalDateTimeValue(addDays(now, 1)));
  const [inclusiveDays, setInclusiveDays] = useState<boolean>(false);

  const start = useMemo(() => parseISO(startValue), [startValue]);
  const end = useMemo(() => parseISO(endValue), [endValue]);

  const [error, setError] = useState<string | null>(null);

  const valid = isValid(start) && isValid(end);

  const swapped = isAfter(start, end);
  const [localStart, localEnd] = swapped ? [end, start] : [start, end];

  const duration = useMemo(() => {
    if (!valid) return null;
    return intervalToDuration({ start: localStart, end: localEnd });
  }, [valid, localStart, localEnd]);

  const totals = useMemo(() => {
    if (!valid) return null;
    return {
      seconds: Math.abs(differenceInSeconds(localEnd, localStart)),
      minutes: Math.abs(differenceInMinutes(localEnd, localStart)),
      hours: Math.abs(differenceInHours(localEnd, localStart)),
      days: Math.abs(differenceInDays(localEnd, localStart)),
      weeks: Math.abs(differenceInWeeks(localEnd, localStart)),
      months: Math.abs(differenceInMonths(localEnd, localStart)),
      years: Math.abs(differenceInYears(localEnd, localStart)),
    } as const;
  }, [valid, localStart, localEnd]);

  const businessDays = useMemo(() => {
    if (!valid) return null;
    const all = eachDayOfInterval({ start: localStart, end: localEnd });
    const count = all.filter((d) => !isWeekend(d)).length;
    return inclusiveDays ? count : Math.max(0, count - 1);
  }, [valid, localStart, localEnd, inclusiveDays]);

  const inclusiveCalendarDays = useMemo(() => {
    if (!valid || !totals) return null;
    return inclusiveDays ? totals.days + 1 : totals.days;
  }, [valid, totals, inclusiveDays]);

  const copyResults = async () => {
    try {
      if (!valid || !duration || !totals) return;
      const lines = [
        `Start: ${format(localStart, "yyyy-MM-dd HH:mm")}`,
        `End:   ${format(localEnd, "yyyy-MM-dd HH:mm")}`,
        swapped ? "(Note: start was after end; values normalized)" : undefined,
        "",
        `Breakdown: ${duration.years ?? 0}y ${duration.months ?? 0}m ${duration.days ?? 0}d ${duration.hours ?? 0}h ${duration.minutes ?? 0}m ${duration.seconds ?? 0}s`,
        `Total: ${totals.years} years | ${totals.months} months | ${totals.weeks} weeks | ${totals.days} days${inclusiveDays ? " (inclusive)" : ""} | ${totals.hours} hours | ${totals.minutes} minutes | ${totals.seconds} seconds`,
        businessDays != null ? `Business days${inclusiveDays ? " (inclusive)" : ""}: ${businessDays}` : undefined,
      ]
        .filter(Boolean)
        .join("\n");
      await navigator.clipboard.writeText(lines);
      setError(null);
    } catch (e: any) {
      setError("Could not copy to clipboard.");
    }
  };

  const quickSet = (type: "+1d" | "+7d" | "+1m" | "+1y") => {
    const base = parseISO(startValue);
    let newEnd = base;
    switch (type) {
      case "+1d":
        newEnd = addDays(base, 1);
        break;
      case "+7d":
        newEnd = addDays(base, 7);
        break;
      case "+1m":
        newEnd = addMonths(base, 1);
        break;
      case "+1y":
        newEnd = addYears(base, 1);
        break;
    }
    setEndValue(toLocalDateTimeValue(newEnd));
  };

  const reset = () => {
    const now = new Date();
    setStartValue(toLocalDateTimeValue(now));
    setEndValue(toLocalDateTimeValue(addDays(now, 1)));
    setInclusiveDays(false);
    setError(null);
  };

  return (
    <div className="mx-auto max-w-5xl p-4 md:p-8 space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Date Difference Calculator</h1>
        <p className="text-muted-foreground mt-2">Compare two dates/times and get a clean breakdown in years, months, days, hours, minutes, seconds — plus totals, weeks, and business days.</p>
      </div>

      <Card className="card-enhanced">
        <CardHeader>
          <CardTitle>Inputs</CardTitle>
          <CardDescription>Select your start and end. We’ll handle any order.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="start">Start (local time)</Label>
              <Input
                id="start"
                type="datetime-local"
                value={startValue}
                onChange={(e) => setStartValue(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end">End (local time)</Label>
              <Input
                id="end"
                type="datetime-local"
                value={endValue}
                onChange={(e) => setEndValue(e.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => quickSet("+1d")}>+1 day</Button>
            <Button variant="outline" size="sm" onClick={() => quickSet("+7d")}>+7 days</Button>
            <Button variant="outline" size="sm" onClick={() => quickSet("+1m")}>+1 month</Button>
            <Button variant="outline" size="sm" onClick={() => quickSet("+1y")}>+1 year</Button>
            <Button variant="ghost" size="sm" onClick={() => { setStartValue(endValue); setEndValue(startValue); }}>
              <ArrowLeftRight className="h-4 w-4 mr-1" /> Swap
            </Button>
            <Button variant="ghost" size="sm" onClick={reset}>
              <RotateCcw className="h-4 w-4 mr-1" /> Reset
            </Button>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Switch id="inclusive" checked={inclusiveDays} onCheckedChange={setInclusiveDays} />
            <div className="space-y-0.5">
              <Label htmlFor="inclusive">Inclusive calendar days</Label>
              <p className="text-xs text-muted-foreground">Counts both start and end dates in the calendar-day total.</p>
            </div>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}
        </CardContent>
      </Card>

      <Card className="card-enhanced">
        <CardHeader>
          <CardTitle>Results</CardTitle>
          <CardDescription>All units at a glance. We also normalize if the end precedes the start.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!valid && (
            <p className="text-red-600">Please provide valid date/time values.</p>
          )}
          {valid && duration && totals && (
            <>
              {isBefore(end, start) && (
                <div className="text-sm text-amber-600">Note: End was before start; values are computed using the absolute interval.</div>
              )}

              <div className="grid gap-4 md:grid-cols-3">
                <div className="p-3 rounded-md bg-muted">
                  <div className="text-xs text-muted-foreground">Start</div>
                  <div className="font-medium">{format(localStart, "PPpp")}</div>
                </div>
                <div className="p-3 rounded-md bg-muted">
                  <div className="text-xs text-muted-foreground">End</div>
                  <div className="font-medium">{format(localEnd, "PPpp")}</div>
                </div>
                <div className="p-3 rounded-md bg-muted">
                  <div className="text-xs text-muted-foreground">Calendar days</div>
                  <div className="font-medium">{inclusiveCalendarDays} day(s) {inclusiveDays ? "(inclusive)" : ""}</div>
                </div>
              </div>

              <Separator />

              <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
                <Stat label="Years" value={duration.years ?? 0} />
                <Stat label="Months" value={duration.months ?? 0} />
                <Stat label="Days" value={duration.days ?? 0} />
                <Stat label="Hours" value={duration.hours ?? 0} />
                <Stat label="Minutes" value={duration.minutes ?? 0} />
                <Stat label="Seconds" value={duration.seconds ?? 0} />
              </div>

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 pt-2">
                <Stat label="Total years" value={totals.years} subtle />
                <Stat label="Total months" value={totals.months} subtle />
                <Stat label="Total weeks" value={totals.weeks} subtle />
                <Stat label="Total days" value={inclusiveCalendarDays ?? totals.days} subtle />
                <Stat label="Total hours" value={totals.hours} subtle />
                <Stat label="Total minutes" value={totals.minutes} subtle />
                <Stat label="Total seconds" value={totals.seconds} subtle />
                <Stat label={`Business days${inclusiveDays ? " (incl.)" : ""}`} value={businessDays ?? 0} subtle />
              </div>

              <div className="pt-2">
                <Button variant="outline" onClick={copyResults}>
                  <Copy className="h-4 w-4 mr-2" /> Copy results
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        Notes: Inputs are interpreted in your local time. Business day counts exclude weekends only. Inclusive mode affects calendar-day and business-day counts.
      </p>
    </div>
  );
}

function Stat({ label, value, subtle = false }: { label: string; value: number | string; subtle?: boolean }) {
  return (
    <div className={`p-3 rounded-md ${subtle ? "bg-muted/60" : "bg-muted"}`}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}
